import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Button, Row, Col, Space, notification } from 'antd';
import {
  ShareAltOutlined,
  FolderAddOutlined,
  HeartOutlined,
  HeartFilled,
} from '@ant-design/icons';

import {
  FormEditButton,
  FormDeleteButton,
  dialogFunction,
  ModalAddToFolder,
} from 'common/components';
import ShareQueryModal from './ShareQueryModal';

import selectorsGridView from 'common/components/grid-view/controllers/selectors';
import * as brandingActions from '@redux/branding/actions';

import { useCheckPermissions } from 'hooks/useCheckPermissions';
import { useGlobalModal, useMutation } from 'hooks';

import * as favoriteServices from 'services/favorite';
import { deleteQuery } from 'services/query';
import { EVENT } from 'static/Constants';

import { useIntl } from 'react-intl';
import Messages from 'i18n/messages/home';
import favoriteMessages from 'i18n/messages/favorite';

import { ABILITY_ACTION, ABILITY_SUBJECT } from 'static/Permission';

const PERMISSION_ADD_TO_FOLDER = [
  { action: ABILITY_ACTION.EDIT, subject: ABILITY_SUBJECT.FOLDER },
];

const GridQueryAction = (props) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const hasPermissionAddToFolder = useCheckPermissions(
    PERMISSION_ADD_TO_FOLDER
  );

  const { selectedQuery, setSelectedQuery, setEditModalVisible } = props;

  const [shareModalVisible, setShareModalVisible] = useState(false);

  const { gridQuery } = useSelector(selectorsGridView.makeSelectGridConfig());

  const { MODAL_NAMES, openModal } = useGlobalModal();

  const [{ status: statusDelete, error: errorDelete }, deleteConfig] =
    useMutation(deleteQuery);

  const onEdit = () => {
    setEditModalVisible(true);
  };

  const onDelete = () => {
    showDeleteConfirmDialog();
  };

  const onShare = () => {
    setShareModalVisible(true);
  };

  const onAddToFolder = () => {
    openModal(MODAL_NAMES.ADD_ITEMS_TO_FOLDER);
  };

  const onFavorite = () => {
    handleAddToFavorite();
  };

  const onUnfavorite = () => {
    handleRemoveFavorite();
  };

  const reloadQueryTable = () => {
    // gridApi.purgeServerSideCache();
    const event = new Event(EVENT.RELOAD_MANAGE_QUERY_TABLE);

    document.dispatchEvent(event);

    setSelectedQuery(null);
  };

  const handleAddToFavorite = async () => {
    try {
      // add to favorite
      const addToFavoriteResponse = await favoriteServices.addToFavorite(
        [selectedQuery?.id],
        selectedQuery?.type
      );
      const { isSuccess, message } = addToFavoriteResponse;
      if (isSuccess) {
        notification.success({
          message: intl.formatMessage(favoriteMessages.addToFavoriteSuccess),
        });
        // update navigation info (favorite total number)
        try {
          // await dispatchUpdateNavigationEvent();
          dispatch(brandingActions.getBrandingNoLoading());
        } catch (error) {
          console.error(
            'get branding info error after add item to favorite: ',
            error
          );
        }
        reloadQueryTable();
      } else {
        addToFavoriteErrorHandler(message);
      }
    } catch (error) {
      addToFavoriteErrorHandler();
    }
  };

  const addToFavoriteErrorHandler = (message) => {
    notification.error({
      message: message
        ? message
        : intl.formatMessage(favoriteMessages.addToFavoriteError),
    });
  };

  const handleRemoveFavorite = async () => {
    try {
      const removeFavorite = await favoriteServices.removeFromFavorite(
        [selectedQuery?.id],
        selectedQuery?.type
      );
      const { isSuccess, message } = removeFavorite;
      if (isSuccess) {
        notification.success({
          message: intl.formatMessage(
            favoriteMessages.removeFromFavoriteSuccess
          ),
        });
        // update navigation info (favorite total number)
        try {
          dispatch(brandingActions.getBrandingNoLoading());
        } catch (error) {
          console.error(
            'get branding info error after add item to favorite: ',
            error
          );
        }
        reloadQueryTable();
      } else {
        removeFromFavoriteErrorHandler(message);
      }
    } catch (error) {
      removeFromFavoriteErrorHandler();
    }
  };

  const removeFromFavoriteErrorHandler = (message) => {
    notification.error({
      message: message
        ? message
        : intl.formatMessage(favoriteMessages.removeFromFavoriteError),
    });
  };

  const showDeleteConfirmDialog = () => {
    dialogFunction({
      type: 'warn',
      content: intl.formatMessage(Messages.deleteQueryConfirm),
      onOk: handleDeleteGridConfig,
    });
  };

  const handleDeleteGridConfig = () => {
    deleteConfig({ listQueries: [selectedQuery.id] });
  };

  useEffect(() => {
    if (statusDelete === 'success') {
      notification.success({ message: 'Deleted successfully' });

      setTimeout(() => {
        reloadQueryTable();
      }, 500);
    }

    if (statusDelete === 'error') {
      notification.success({ message: errorDelete });
    }

    return;
  }, [errorDelete, statusDelete]);

  const ShareButton = Button;
  const AddToFolderButton = Button;
  const FavoriteButton = Button;
  const UnfavoriteButton = Button;

  const disabledShareBtn = !selectedQuery;
  const disabledAddToFolderBtn = !selectedQuery;
  const disabledFavoriteBtn = !selectedQuery || !selectedQuery?.shareable;
  const disabledUnfavoriteBtn = !selectedQuery || !selectedQuery?.shareable;
  const disabledEditBtn = !selectedQuery || !selectedQuery.editable;
  const disabledDeleteBtn = !selectedQuery || !selectedQuery.editable;

  const buttonStyle = { borderRadius: 4 };

  return (
    <Row justify='end' style={{ marginBottom: 10 }}>
      {gridQuery.length === 0 ? null : (
        <>
          <Col flex={0}>
            <Space size={4}>
              <ShareButton
                type='primary'
                icon={<ShareAltOutlined />}
                style={buttonStyle}
                onClick={onShare}
                disabled={disabledShareBtn}
              >
                {intl.formatMessage(Messages.share)}
              </ShareButton>

              {hasPermissionAddToFolder && (
                <AddToFolderButton
                  type='primary'
                  icon={<FolderAddOutlined />}
                  style={buttonStyle}
                  onClick={onAddToFolder}
                  disabled={disabledAddToFolderBtn}
                >
                  {intl.formatMessage(Messages.configAddToFolder)}
                </AddToFolderButton>
              )}

              {selectedQuery?.isFavorited ? (
                <UnfavoriteButton
                  type='primary'
                  danger
                  icon={<HeartFilled />}
                  style={buttonStyle}
                  onClick={onUnfavorite}
                  disabled={disabledUnfavoriteBtn}
                >
                  {intl.formatMessage(favoriteMessages.removeFromFavorite)}
                </UnfavoriteButton>
              ) : (
                <FavoriteButton
                  type='primary'
                  icon={<HeartOutlined />}
                  style={buttonStyle}
                  onClick={onFavorite}
                  disabled={disabledFavoriteBtn}
                >
                  {intl.formatMessage(favoriteMessages.addToFavorite)}
                </FavoriteButton>
              )}
            </Space>
          </Col>
          <Col flex={1}></Col>
          <Col flex={0}>
            <Space size={4}>
              <FormEditButton onClick={onEdit} disabled={disabledEditBtn} />
              <FormDeleteButton
                onClick={onDelete}
                disabled={disabledDeleteBtn}
              />
            </Space>
          </Col>
        </>
      )}
      <ShareQueryModal
        data={selectedQuery}
        visible={shareModalVisible}
        setVisible={setShareModalVisible}
      />
      <ModalAddToFolder
        dataItem={selectedQuery}
        dataType='query'
        hasPermissionAddToFolder={hasPermissionAddToFolder}
      />
    </Row>
  );
};

export default GridQueryAction;
